import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Idle timeout: 30 minutes
const IDLE_TIMEOUT_MS = 30 * 60 * 1000;
// Warning before timeout: 2 minutes before
const IDLE_WARNING_MS = 28 * 60 * 1000;
// Auto-refresh interval: every 50 minutes (before 1h access token expires)
const REFRESH_INTERVAL_MS = 50 * 60 * 1000;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionWarning, setSessionWarning] = useState(false);

  // Access token stored in memory only (not localStorage)
  const accessTokenRef = useRef(null);
  const refreshIntervalRef = useRef(null);
  const idleTimerRef = useRef(null);
  const warningTimerRef = useRef(null);

  // Configure axios defaults
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  axios.defaults.baseURL = API_BASE_URL;
  axios.defaults.withCredentials = true; // Send cookies with requests

  // ----------------------------------------------------------
  // Token management
  // ----------------------------------------------------------

  const setAccessToken = (token) => {
    accessTokenRef.current = token;
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  };

  const clearSession = useCallback(() => {
    setAccessToken(null);
    setUser(null);
    setSessionWarning(false);
    clearInterval(refreshIntervalRef.current);
    clearTimeout(idleTimerRef.current);
    clearTimeout(warningTimerRef.current);
    // Clear legacy localStorage entries
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }, []);

  // ----------------------------------------------------------
  // Refresh token flow
  // ----------------------------------------------------------

  const refreshAccessToken = useCallback(async () => {
    try {
      const response = await axios.post('/auth/refresh');
      const { token, user: userData } = response.data;
      setAccessToken(token);
      setUser(userData);
      return true;
    } catch (error) {
      console.log('Token refresh failed — logging out');
      clearSession();
      return false;
    }
  }, [clearSession]);

  // ----------------------------------------------------------
  // Axios interceptor: auto-refresh on 401
  // ----------------------------------------------------------

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // If we get a 401 and haven't retried yet, try refreshing
        if (
          error.response?.status === 401 &&
          !originalRequest._retry &&
          !originalRequest.url.includes('/auth/refresh') &&
          !originalRequest.url.includes('/auth/google')
        ) {
          originalRequest._retry = true;
          const refreshed = await refreshAccessToken();
          if (refreshed) {
            // Retry the original request with the new token
            originalRequest.headers['Authorization'] = `Bearer ${accessTokenRef.current}`;
            return axios(originalRequest);
          }
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [refreshAccessToken]);

  // ----------------------------------------------------------
  // Idle timeout
  // ----------------------------------------------------------

  const resetIdleTimer = useCallback(() => {
    if (!user) return;

    setSessionWarning(false);
    clearTimeout(idleTimerRef.current);
    clearTimeout(warningTimerRef.current);

    // Show warning at 28 minutes
    warningTimerRef.current = setTimeout(() => {
      setSessionWarning(true);
    }, IDLE_WARNING_MS);

    // Logout at 30 minutes
    idleTimerRef.current = setTimeout(() => {
      console.log('Idle timeout — logging out');
      logout();
    }, IDLE_TIMEOUT_MS);
  }, [user]);

  useEffect(() => {
    if (!user) return;

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    // Throttle: only reset every 30 seconds max
    let lastReset = Date.now();
    const throttledReset = () => {
      if (Date.now() - lastReset > 30000) {
        lastReset = Date.now();
        resetIdleTimer();
      }
    };

    events.forEach((event) => window.addEventListener(event, throttledReset));
    resetIdleTimer(); // Start the timer

    return () => {
      events.forEach((event) => window.removeEventListener(event, throttledReset));
      clearTimeout(idleTimerRef.current);
      clearTimeout(warningTimerRef.current);
    };
  }, [user, resetIdleTimer]);

  // ----------------------------------------------------------
  // Auto-refresh interval (every 50 minutes)
  // ----------------------------------------------------------

  useEffect(() => {
    if (!user) {
      clearInterval(refreshIntervalRef.current);
      return;
    }

    refreshIntervalRef.current = setInterval(() => {
      refreshAccessToken();
    }, REFRESH_INTERVAL_MS);

    return () => clearInterval(refreshIntervalRef.current);
  }, [user, refreshAccessToken]);

  // ----------------------------------------------------------
  // Initial mount: try to restore session via refresh token cookie
  // ----------------------------------------------------------

  useEffect(() => {
    const initSession = async () => {
      // Check for legacy localStorage token first (migration path)
      const legacyToken = localStorage.getItem('token');
      if (legacyToken) {
        // Try to verify the legacy token
        setAccessToken(legacyToken);
        try {
          const response = await axios.get('/auth/verify');
          if (response.data.valid) {
            setUser(response.data.user);
            // Clear legacy storage — session is now cookie-based
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setLoading(false);
            return;
          }
        } catch (e) {
          // Legacy token invalid — fall through to refresh
          setAccessToken(null);
        }
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }

      // Try to get a new access token via the refresh token cookie
      try {
        const response = await axios.post('/auth/refresh');
        const { token, user: userData } = response.data;
        setAccessToken(token);
        setUser(userData);
      } catch (error) {
        // No valid refresh token — user needs to log in
        clearSession();
      } finally {
        setLoading(false);
      }
    };

    initSession();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ----------------------------------------------------------
  // Login
  // ----------------------------------------------------------

  const login = async (googleToken) => {
    try {
      const response = await axios.post('/auth/google', { token: googleToken });
      const { token, user: userData } = response.data;

      // Store access token in memory only
      setAccessToken(token);
      setUser(userData);

      return { success: true };
    } catch (error) {
      console.error('Login failed:', error);
      clearSession();

      let errorMessage = 'Login failed';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      return { success: false, error: errorMessage };
    }
  };

  // ----------------------------------------------------------
  // Logout
  // ----------------------------------------------------------

  const logout = async () => {
    try {
      await axios.post('/auth/logout');
    } catch (error) {
      // Best-effort server-side logout
      console.error('Logout API call failed:', error);
    }
    clearSession();
  };

  // ----------------------------------------------------------
  // Keep session alive (dismiss warning)
  // ----------------------------------------------------------

  const keepAlive = () => {
    setSessionWarning(false);
    resetIdleTimer();
    refreshAccessToken();
  };

  const value = {
    user,
    login,
    logout,
    loading,
    sessionWarning,
    keepAlive,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
